const TOKEN_KEY = 'macrodash_access_token';
const USER_KEY = 'macrodash_user';

export interface AuthUser {
  id: string;
  email: string;
  created_at?: string;
}

type AuthListener = (user: AuthUser | null) => void;

const listeners = new Set<AuthListener>();

function notify(user: AuthUser | null) {
  for (const listener of listeners) {
    try {
      listener(user);
    } catch {
      // ignore listener errors
    }
  }
}

function getApiBase(): string {
  return (import.meta.env.VITE_API_URL as string | undefined) ?? '';
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function setSession(accessToken: string, user: AuthUser): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  notify(user);
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  notify(null);
}

export function onAuthChange(listener: AuthListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Request a 6-digit email OTP */
export async function login(email: string): Promise<void> {
  const base = getApiBase();
  if (!base) throw new Error('VITE_API_URL is not configured');

  const response = await fetch(`${base}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(
      (body as { message?: string }).message ||
        `Login failed: ${response.status}`
    );
  }
}

/** Verify OTP code and store JWT session */
export async function verifyOtp(email: string, code: string): Promise<AuthUser> {
  const base = getApiBase();
  if (!base) throw new Error('VITE_API_URL is not configured');

  const response = await fetch(`${base}/auth/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code: code.trim() }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(
      (body as { message?: string }).message ||
        `Verification failed: ${response.status}`
    );
  }

  const data = (await response.json()) as {
    access_token: string;
    user: AuthUser;
  };

  setSession(data.access_token, data.user);
  return data.user;
}

/** @deprecated use verifyOtp — kept for AuthCallback magic-link URLs if any */
export async function verifyMagicLink(
  token: string,
  email: string
): Promise<AuthUser> {
  return verifyOtp(email, token);
}

/** Validate stored JWT and refresh user from API */
export async function fetchMe(): Promise<AuthUser | null> {
  const token = getToken();
  if (!token) {
    clearSession();
    return null;
  }

  const base = getApiBase();
  if (!base) return getUser();

  try {
    const response = await fetch(`${base}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      clearSession();
      return null;
    }

    const data = (await response.json()) as { user: AuthUser };
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    notify(data.user);
    return data.user;
  } catch {
    return getUser();
  }
}

export async function signOut(): Promise<void> {
  clearSession();
}
